import { useCallback, useMemo } from 'react';
import { ButtonWithIcon } from 'src/components/Buttons';
import { FilterPillList } from 'src/components/common/FilterPill';
import { Content } from 'src/contexts/ContentContext';
import { vars } from 'src/core/themes';
import { Button } from 'src/core/ui';
import { SectionSelectDialog } from 'src/dialogs/SectionSelectDialog';
import { useBasicDialog } from 'src/hooks/useBasicDialog';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { IconsFill, PlusIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { Feature, SectionInfo } from 'src/WebApiController';

import { useAutoPricingSettingsSelectedSections } from './AutoPricingSelectedSectionSettingsInput.hooks';

export const AutoPricingSelectedSectionSettingsInput = ({
  venueMapSections,
  disabled,
}: {
  venueMapSections: SectionInfo[] | null | undefined;
  disabled: boolean;
}) => {
  const sectionSelectDialog = useBasicDialog();

  const hasAutoPricingEmptySelectedSectionsNoCompsFeature = useUserHasFeature(
    Feature.AutoPricingEmptySelectedSectionsNoComps
  );

  const {
    compListingSelectedSectionSettings,
    setCompListingSelectedSectionSettings,
    selectedSections,
    rowIdFilters,
    sectionRowIdFilters,
    onRowIdFiltersChange,
    onSectionIdFiltersChange,
  } = useAutoPricingSettingsSelectedSections(venueMapSections);

  const sectionOnlyFilterPillList = useMemo(() => {
    return compListingSelectedSectionSettings?.rowOffset != null;
  }, [compListingSelectedSectionSettings?.rowOffset]);

  const handleSelectAllSections = useCallback(() => {
    if (!venueMapSections) {
      return;
    }

    const currentSectionCount =
      compListingSelectedSectionSettings?.sectionIdFilter?.length ?? 0;

    if (currentSectionCount > 0) {
      setCompListingSelectedSectionSettings({
        ...compListingSelectedSectionSettings,
        sectionIdFilter: [],
        rowIdFilter: [],
        sectionRowIdFilter: {},
      });
    } else {
      setCompListingSelectedSectionSettings({
        ...compListingSelectedSectionSettings,
        sectionIdFilter: venueMapSections.map((s) => s.id),
        rowIdFilter: [],
        sectionRowIdFilter: {},
      });
    }
  }, [
    venueMapSections,
    compListingSelectedSectionSettings,
    setCompListingSelectedSectionSettings,
  ]);

  return (
    <>
      {compListingSelectedSectionSettings?.sectionIdFilter?.length ? (
        <>
          <div style={{ flex: 1, width: '600px', overflow: 'auto' }}>
            <FilterPillList
              selectedSections={selectedSections}
              rowIdFilters={rowIdFilters}
              sectionRowIdFilters={sectionRowIdFilters}
              applyRowFilterChanges={onRowIdFiltersChange}
              applySelectedSectionChanges={onSectionIdFiltersChange}
              disabled={disabled}
              sectionOnly={sectionOnlyFilterPillList}
              allowRowRange
            />
          </div>

          {hasAutoPricingEmptySelectedSectionsNoCompsFeature ? (
            <>
              {venueMapSections && (
                <Button
                  variant="textPlain"
                  shape="none"
                  onClick={handleSelectAllSections}
                  disabled={disabled}
                >
                  <Content id={ContentId.ClearAll} />
                </Button>
              )}
            </>
          ) : (
            <>
              <ButtonWithIcon
                variant="textPlain"
                shape="none"
                icon={
                  <PlusIcon
                    fill={IconsFill.textBrand}
                    size={vars.iconSize.s}
                    withHoverEffect
                  />
                }
                textContentId={ContentId.EditSections}
                onClick={sectionSelectDialog.launchDialog}
                disabled={disabled}
                style={{ padding: 0 }}
              />
            </>
          )}
        </>
      ) : (
        venueMapSections && (
          <>
            {hasAutoPricingEmptySelectedSectionsNoCompsFeature ? (
              <Button
                variant="textPlain"
                shape="none"
                onClick={handleSelectAllSections}
                disabled={disabled}
              >
                <Content id={ContentId.SelectAll} />
              </Button>
            ) : (
              <>
                <ButtonWithIcon
                  variant="textPlain"
                  shape="none"
                  icon={
                    <PlusIcon
                      fill={IconsFill.textBrand}
                      size={vars.iconSize.s}
                      withHoverEffect
                    />
                  }
                  textContentId={ContentId.SelectSections}
                  onClick={sectionSelectDialog.launchDialog}
                  disabled={disabled}
                  style={{ padding: 0 }}
                />
              </>
            )}
          </>
        )
      )}

      <SectionSelectDialog
        {...sectionSelectDialog.dialogProps}
        initialSectionIds={
          compListingSelectedSectionSettings?.sectionIdFilter ?? []
        }
        onSave={(selectedSections) => {
          const compListingSelectedSectionSettingsNew = {
            ...compListingSelectedSectionSettings,
            sectionIdFilter: selectedSections.map((s) => s.id),
            rowIdFilter: compListingSelectedSectionSettings?.rowIdFilter ?? [],
            sectionRowIdFilter:
              compListingSelectedSectionSettings?.sectionRowIdFilter ?? {},
          };
          setCompListingSelectedSectionSettings(
            compListingSelectedSectionSettingsNew
          );
          sectionSelectDialog.closeDialog();
        }}
        onClosed={sectionSelectDialog.closeDialog}
      />
    </>
  );
};
